/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import {
  ElectricUnit,
  GasUnit,
  KPI_PERCENTAGE_DECIMAL_PRECISION,
  KPI_VALUE_DECIMAL_PRECISION,
  EnergyUsageScopeType,
  EnergyUsagesDetailExpandMeterField,
  FuelColorMappingFor6,
  FuelColorMapping,
  IntervalType,
  MAGIC_VALUE,
  PERCENT_CHANGE_THRESHOLD,
  WaterUnit
} from './Constants';
import { IEnergyMetersData } from './ICarbonViewDetailsUtils';
import {
  getConvertedValue,
  getMaxConvertedValueFromArray
} from './unitHandling/unitHandling';
import { ConversionType } from './unitHandling/UnitModel';
import {
  AreaWiseAggregations,
  EnergyDisaggregation,
  EnergyUsageAggregation,
  IAggregationEnergyData,
  IAggregationSummaryData,
  IAny,
  IBasicMonthWise,
  IBreakdownByMeterProps,
  IEmissionsByUtility,
  IEnergyBreakdownMeter,
  IEnergyMeter,
  IMonthwiseStackedChartData,
  IMonthwiseStackedChartDataEnergy,
  IObject,
  IRequestContext,
  ISummaryData
} from '../../DashboardKPIWidgets/IEnergyManagementProps';
import {
  checkStartEndDateDays,
  checkStartEndDateHourly,
  checkStartEndDateMonth,
  excludeCurrentMonth,
  formatCurrency,
  formatPriceValue,
  formattedDate,
  getAbsoluteValue,
  getFirstDayofSelectedMonth,
  graphValue,
  graphValueNILM,
  graphValueNILMHourly,
  returnUTCTimeFormat,
  tableValueNILM,
  tableValueNILMHourly
} from './Utils';
import {
  IEnergyUtilityMeters,
  ITotalConsumeUtilitydetails,
  IUtilityArea,
  IMeterEnergyData,
  IEnergyMeterDetails,
  flatArry,
  IEnergyDisaggregatedData,
  IEnergyDisaggregatedConvertedData,
  IEnergyUsageTableDataNILM
} from '../../DetailKPIWidgets/EnergyUsagesViewDetailsWidget/AllUtilitiesDetails/IEnergyUsagesBreakdownProps';
import { getEnergyTotalUsagesValues } from './unitHandling/EmaasSiteKpiDataScaling';
import { EmaasSubscriptionType, EnergyUsageField } from '../Enums';
import descriptors from '../../../locales/en-US/descriptors';
import { TFunction } from '@scuf/localization/dist/types';
import { ISiteEnergyUsageData } from '../../../main';
import {
  IDataTableHeader,
  IPortfolioMeterDataProps
} from '../BreakdownByMeter/BreakdownByMeterProps';
import moment from 'moment';
import { handleEuiUnitConversion } from './unitHandling/EuiUnitHandling';
import { scaledData } from '../ProgressBar/IProgressBarWidget';
import {
  getColorIndex,
  getMonthwiseDataForElectricity,
  getMonthwiseDataForEnergyDisaggregation,
  getMonthwiseDataForFual
} from './CarbonViewDetailsUtils';
import { getIntervalTypeForEnergyDisaggregation } from './PortfolioEnergy/PortfolioEnergyVDUtils';
import { UnitUtility } from '@bmsp/bmsp.web.component.common';

const d = descriptors.energyManagement;

export interface IElectricityMeters {
  meterId: string;
  meterName: string;
  utilityType: string;
  totalActualEmission: number;
  totalPreviousPeriodEmission: number;
  totalDeviationPercentage: number;
  totalDeviation: number;
  unit: string;
}

export const getEnergyUsageBreakdownByMeterValuesArray = (
  breakdownByMeter: flatArry[]
) => {
  return breakdownByMeter?.map((meter: flatArry) => {
    if (meter && (meter.totalBaselineConsumption || meter.totalConsumption)) {
      return Math.max(
        meter.totalConsumption as number,
        meter.totalBaselineConsumption as number
      );
    } else if (meter && (meter.baselineConsumption || meter.totalConsumption)) {
      return Math.max(
        meter.totalConsumption as number,
        meter.baselineConsumption as number
      );
    } else {
      return 0;
    }
  });
};

export const getDisaggregatedDataForNILM = (
  breakdownByMeter: Array<IEnergyUsageTableDataNILM> | undefined,
  utilityUnit: string,
  maxConversionUnit: string,
  fromUnit?: string
) => {
  const disaggregatedMeters: Array<IEnergyMetersData> = [];
  breakdownByMeter?.forEach((data) => {
    let {
      id,
      name,
      totalConsumption,
      totalBaselineConsumption,
      totalEnergyDeviation,
      totalEnergyDeviationPercentage,
      totalAbsoluteConsumption,
      totalAbsoluteBaselineConsumption
    } = data;
    let meterSavings: IEnergyMetersData = {
      meterId: id,
      meterName: name,
      totalActualEmission: totalConsumption,
      actualUnit: getMaxConvertedValueFromArray(
        ConversionType.ELECTRICITY,
        fromUnit ? fromUnit : utilityUnit,
        [totalConsumption],
        utilityUnit
      ).Unit,
      baselineUnit: getMaxConvertedValueFromArray(
        ConversionType.ELECTRICITY,
        fromUnit ? fromUnit : utilityUnit,
        [totalBaselineConsumption],
        utilityUnit
      ).Unit,
      changeUnit: getMaxConvertedValueFromArray(
        ConversionType.ELECTRICITY,
        fromUnit ? fromUnit : utilityUnit,
        [totalEnergyDeviation],
        utilityUnit
      ).Unit,
      totalPreviousPeriodEmission: totalBaselineConsumption,
      totalAbsoluteConsumption: getConvertedValuesViewDetails(
        totalAbsoluteConsumption,
        ConversionType.ELECTRICITY,
        utilityUnit,
        maxConversionUnit
      ),
      totalAbsoluteBaselineConsumption: getConvertedValuesViewDetails(
        totalAbsoluteBaselineConsumption,
        ConversionType.ELECTRICITY,
        utilityUnit,
        maxConversionUnit
      ),
      totalDeviationPercentage:
        totalEnergyDeviationPercentage === null
          ? null
          : totalEnergyDeviationPercentage,
      totalDeviation: totalEnergyDeviation,
      rawTotalActualEmission: totalConsumption,
      rawTotalPreviousPeriodEmission: totalBaselineConsumption,
      rawTotalAbsoluteConsumption: totalAbsoluteConsumption,
      rawTotalAbsoluteBaselineConsumption: totalAbsoluteBaselineConsumption,
      rawTotalDeviation: totalEnergyDeviation,
      unit: maxConversionUnit,
      utilityType: 'Electric'
    };
    disaggregatedMeters.push(meterSavings);
  });
  return disaggregatedMeters;
};

export interface IFlattenedNILMMEterWiseAggregation
  extends IEnergyUsageTableDataNILM {
  baselineConsumption: number;
  meterId: string;
  meterName: string;
  totalConsumption: number;
}

export const getMeterWiseAggregationsForNILM = (
  breakdownByMeter: Array<IEnergyUsageTableDataNILM> | undefined | null | IAny,
  utilityUnit: string,
  flatMeterWiseAggregations: flatArry[],
  originalUnit: string,
  showChangeWhenData: boolean = false
  //conversionUnit: IObject
) => {
  const valuesArray = getEnergyUsageBreakdownByMeterValuesArray(
    flatMeterWiseAggregations
  );
  const maxConversionUnit = getMaxConvertedValueFromArray(
    ConversionType.ELECTRICITY,
    utilityUnit,
    valuesArray,
    originalUnit
  ).Unit;
  const electricityMeters: Array<IEnergyMetersData> = [];
  breakdownByMeter?.forEach((data: IEnergyUsageTableDataNILM) => {
    let {
      sourceType,
      id,
      name,
      totalConsumption,
      totalBaselineConsumption,
      totalEnergyDeviation,
      totalEnergyDeviationPercentage,
      totalAbsoluteConsumption,
      totalAbsoluteBaselineConsumption,
      disaggregation
    } = data;
    sourceType = sourceType.toLowerCase();

    if (sourceType.includes(ElectricUnit)) {
      const hasBaseline =
        showChangeWhenData &&
        (isNaN(Number(totalConsumption)) ||
          isNaN(Number(totalBaselineConsumption)) ||
          Number(totalConsumption) === 0 ||
          Number(totalBaselineConsumption) === 0 ||
          Math.abs(Number(totalEnergyDeviationPercentage)) >
            PERCENT_CHANGE_THRESHOLD);

      let meterSavings: IEnergyMetersData = {
        meterId: id,
        meterName: name,
        utilityType: name,
        totalActualEmission: totalConsumption,
        totalPreviousPeriodEmission: totalBaselineConsumption,
        actualUnit: getMaxConvertedValueFromArray(
          ConversionType.ELECTRICITY,
          utilityUnit,
          [totalConsumption],
          originalUnit
        ).Unit,
        baselineUnit: getMaxConvertedValueFromArray(
          ConversionType.ELECTRICITY,
          utilityUnit,
          [totalBaselineConsumption],
          originalUnit
        ).Unit,
        changeUnit: getMaxConvertedValueFromArray(
          ConversionType.ELECTRICITY,
          utilityUnit,
          [totalEnergyDeviation],
          originalUnit
        ).Unit,
        totalAbsoluteConsumption: getConvertedValuesViewDetails(
          totalAbsoluteConsumption,
          ConversionType.ELECTRICITY,
          originalUnit, //utilityUnit,
          maxConversionUnit
        ),
        totalAbsoluteBaselineConsumption: getConvertedValuesViewDetails(
          totalAbsoluteBaselineConsumption,
          ConversionType.ELECTRICITY,
          originalUnit, //utilityUnit,
          maxConversionUnit
        ),
        totalDeviationPercentage:
          totalEnergyDeviationPercentage === null
            ? null
            : totalEnergyDeviationPercentage,
        totalDeviation: hasBaseline ? null : totalEnergyDeviation,
        rawTotalActualEmission: totalConsumption,
        rawTotalPreviousPeriodEmission: totalBaselineConsumption,
        rawTotalAbsoluteConsumption: totalAbsoluteConsumption,
        rawTotalAbsoluteBaselineConsumption: totalAbsoluteBaselineConsumption,
        rawTotalDeviation: totalEnergyDeviation,
        unit: maxConversionUnit,
        utilityMeters: getDisaggregatedDataForNILM(
          disaggregation,
          originalUnit, //utilityUnit,
          maxConversionUnit,
          utilityUnit
        )
      };
      if (meterSavings.totalDeviation === 0 && totalEnergyDeviation !== 0) {
        meterSavings = {
          ...meterSavings,
          deviationScaledData: getConvertedValue(
            ConversionType?.ELECTRICITY,
            originalUnit, //utilityUnit,
            totalEnergyDeviation,
            maxConversionUnit
          )
        };
      }
      electricityMeters.push(meterSavings);
    }
  });

  return { data: electricityMeters, unit: maxConversionUnit };
};

export const getUtilityTypeMeterEnergyDetails = (
  breakdownByMeter: Array<IEnergyMeter> | undefined,
  utilityUnit: IObject,
  fromUnit: string,
  showChangeWhenData: boolean = false
  //conversionUnit: IObject
) => {
  const electricityMeters: Array<IEnergyMetersData> = [];
  const gasMeters: Array<IEnergyMetersData> = [];
  const waterMeters: Array<IEnergyMetersData> = []; // Prepare to collect water meters data
  breakdownByMeter?.forEach((data) => {
    let {
      sourceType,
      id,
      name,
      totalConsumption,
      totalBaselineConsumption,
      totalEnergyDeviation,
      totalEnergyDeviationPercentage,
      totalAbsoluteConsumption,
      totalAbsoluteBaselineConsumption
    } = data;
    sourceType = sourceType.toLowerCase();
    const maxConversionUnit = getMaxConvertedValueFromArray(
      sourceType === 'gas'
        ? ConversionType.GAS
        : sourceType === 'water'
        ? ConversionType.WATER
        : ConversionType.ELECTRICITY,
      sourceType === 'gas' ? utilityUnit[sourceType] : fromUnit, //utilityUnit[sourceType],
      [totalConsumption, totalBaselineConsumption],
      utilityUnit[sourceType]
    ).Unit;
    const hasBaleine =
      showChangeWhenData &&
      (isNaN(Number(totalConsumption)) ||
        isNaN(Number(totalBaselineConsumption)) ||
        Number(totalConsumption).toFixed(0) === '0' ||
        Number(totalBaselineConsumption).toFixed(0) === '0' ||
        Math.abs(Number(totalEnergyDeviationPercentage)) >
          PERCENT_CHANGE_THRESHOLD);

    if (sourceType.includes(ElectricUnit)) {
      let meterSavings: IEnergyMetersData = {
        meterId: id,
        meterName: name,
        utilityType: sourceType,
        totalActualEmission: totalConsumption,
        totalPreviousPeriodEmission: totalBaselineConsumption,
        totalAbsoluteConsumption: getConvertedValuesViewDetails(
          totalAbsoluteConsumption,
          ConversionType.ELECTRICITY,
          utilityUnit[sourceType],
          maxConversionUnit
        ),
        totalAbsoluteBaselineConsumption: getConvertedValuesViewDetails(
          totalAbsoluteBaselineConsumption,
          ConversionType.ELECTRICITY,
          utilityUnit[sourceType],
          maxConversionUnit
        ),
        totalDeviationPercentage:
          totalEnergyDeviationPercentage === null
            ? null
            : totalEnergyDeviationPercentage,
        totalDeviation: hasBaleine ? null : totalEnergyDeviation,
        actualUnit: getMaxConvertedValueFromArray(
          sourceType === 'gas'
            ? ConversionType.GAS
            : ConversionType.ELECTRICITY,
          sourceType === 'gas' ? utilityUnit[sourceType] : fromUnit, //utilityUnit[sourceType],
          [totalConsumption],
          utilityUnit[sourceType]
        ).Unit,
        baselineUnit: getMaxConvertedValueFromArray(
          sourceType === 'gas'
            ? ConversionType.GAS
            : ConversionType.ELECTRICITY,
          sourceType === 'gas' ? utilityUnit[sourceType] : fromUnit, //utilityUnit[sourceType],
          [totalBaselineConsumption],
          utilityUnit[sourceType]
        ).Unit,
        changeUnit: getMaxConvertedValueFromArray(
          sourceType === 'gas'
            ? ConversionType.GAS
            : ConversionType.ELECTRICITY,
          sourceType === 'gas' ? utilityUnit[sourceType] : fromUnit, //utilityUnit[sourceType],
          [totalEnergyDeviation],
          utilityUnit[sourceType]
        ).Unit,
        rawTotalActualEmission: totalConsumption,
        rawTotalPreviousPeriodEmission: totalBaselineConsumption,
        rawTotalAbsoluteConsumption: totalAbsoluteConsumption,
        rawTotalAbsoluteBaselineConsumption: totalAbsoluteBaselineConsumption,
        rawTotalDeviation: totalEnergyDeviation,
        unit: maxConversionUnit
      };
      if (meterSavings.totalDeviation === 0 && totalEnergyDeviation !== 0) {
        meterSavings = {
          ...meterSavings,
          deviationScaledData: getConvertedValue(
            ConversionType?.ELECTRICITY,
            utilityUnit[sourceType], // originalUnit,
            totalEnergyDeviation,
            maxConversionUnit //utilityUnit[sourceType]
          )
        };
      }
      electricityMeters.push(meterSavings);
    }

    if (sourceType.includes(GasUnit)) {
      let meterSavings: IEnergyMetersData = {
        meterId: id,
        meterName: name,
        utilityType: sourceType,
        totalActualEmission: totalConsumption,
        totalPreviousPeriodEmission: totalBaselineConsumption,
        totalAbsoluteConsumption: getConvertedValuesViewDetails(
          totalAbsoluteConsumption,
          ConversionType.GAS,
          utilityUnit[sourceType],
          maxConversionUnit
        ),
        totalAbsoluteBaselineConsumption: getConvertedValuesViewDetails(
          totalAbsoluteBaselineConsumption,
          ConversionType.GAS,
          utilityUnit[sourceType],
          maxConversionUnit
        ),
        totalDeviationPercentage:
          totalEnergyDeviationPercentage === null
            ? null
            : totalEnergyDeviationPercentage,
        totalDeviation: hasBaleine ? null : totalEnergyDeviation,
        actualUnit: getMaxConvertedValueFromArray(
          sourceType === 'gas'
            ? ConversionType.GAS
            : ConversionType.ELECTRICITY,
          sourceType === 'gas' ? utilityUnit[sourceType] : fromUnit, //utilityUnit[sourceType],
          [totalConsumption],
          utilityUnit[sourceType]
        ).Unit,
        baselineUnit: getMaxConvertedValueFromArray(
          sourceType === 'gas'
            ? ConversionType.GAS
            : ConversionType.ELECTRICITY,
          sourceType === 'gas' ? utilityUnit[sourceType] : fromUnit, //utilityUnit[sourceType],
          [totalBaselineConsumption],
          utilityUnit[sourceType]
        ).Unit,
        changeUnit: getMaxConvertedValueFromArray(
          sourceType === 'gas'
            ? ConversionType.GAS
            : ConversionType.ELECTRICITY,
          sourceType === 'gas' ? utilityUnit[sourceType] : fromUnit, //utilityUnit[sourceType],
          [totalEnergyDeviation],
          utilityUnit[sourceType]
        ).Unit,
        rawTotalActualEmission: totalConsumption,
        rawTotalPreviousPeriodEmission: totalBaselineConsumption,
        rawTotalAbsoluteConsumption: totalAbsoluteConsumption,
        rawTotalAbsoluteBaselineConsumption: totalAbsoluteBaselineConsumption,
        rawTotalDeviation: totalEnergyDeviation,
        unit: maxConversionUnit
      };
      if (meterSavings.totalDeviation === 0 && totalEnergyDeviation !== 0) {
        meterSavings = {
          ...meterSavings,
          deviationScaledData: getConvertedValue(
            ConversionType.GAS,
            utilityUnit[sourceType],
            totalEnergyDeviation
          )
        };
      }
      gasMeters.push(meterSavings);
    }
    if (sourceType.includes(WaterUnit)) {
      // Assuming 'water' is the identifier for water utilities
      let meterSavings: IEnergyMetersData = {
        utilityType: sourceType,
        // Other properties similar to gas and electricity meters
        totalAbsoluteConsumption: getConvertedValuesViewDetails(
          totalAbsoluteConsumption,
          ConversionType.WATER,
          utilityUnit[sourceType],
          maxConversionUnit
        ),
        totalAbsoluteBaselineConsumption: getConvertedValuesViewDetails(
          totalAbsoluteBaselineConsumption,
          ConversionType.WATER,
          utilityUnit[sourceType],
          maxConversionUnit
        ),
        meterId: id,
        totalActualEmission: totalConsumption,
        totalPreviousPeriodEmission: totalBaselineConsumption,
        totalDeviationPercentage:
          totalEnergyDeviationPercentage === null
            ? null
            : totalEnergyDeviationPercentage,
        totalDeviation: getConvertedValuesViewDetails(
          totalEnergyDeviation,
          ConversionType.WATER,
          utilityUnit[sourceType],
          maxConversionUnit
        ),
        rawTotalActualEmission: totalConsumption,
        rawTotalPreviousPeriodEmission: totalBaselineConsumption,
        rawTotalAbsoluteConsumption: totalConsumption,
        rawTotalAbsoluteBaselineConsumption: totalBaselineConsumption,
        rawTotalDeviation: totalEnergyDeviation
      };
      if (meterSavings.totalDeviation === 0 && totalEnergyDeviation !== 0) {
        meterSavings = {
          ...meterSavings,
          deviationScaledData: {
            ...getConvertedValue(
              ConversionType.WATER,
              utilityUnit[sourceType],
              totalEnergyDeviation
            ),
            Value:
              getConvertedValue(
                ConversionType.WATER,
                utilityUnit[sourceType],
                totalEnergyDeviation
              ).Value ?? null
          }
        };
      }
      console.log('Processed waterMeter:', meterSavings);
      waterMeters.push(meterSavings);
    }
  });

  return { electricityMeters, gasMeters, waterMeters };
};

const getEnergyUsageScope = (
  element: IMeterEnergyData,
  utilityTypeMeterDetails: IEnergyMeterDetails,
  utilityMeterDetail: Array<IUtilityArea>,
  fromUnit: string,
  showChangeWhenData: boolean = false
) => {
  const valuesArray = [
    element.totalConsumption,
    element.totalBaselineConsumption
  ];

  if (
    element.utilityType.toLowerCase() === EnergyUsageScopeType.ELECTRIC ||
    element.utilityType === EnergyUsageScopeType.ELECTRICITY
  ) {
    const maxValueUnitConversion = getMaxConvertedValueFromArray(
      ConversionType.ELECTRICITY,
      fromUnit, //element.unit,
      valuesArray
    );
    const convertElectricValue = (value: number | null) => {
      return getConvertedValuesViewDetails(
        value,
        ConversionType?.ELECTRICITY,
        element?.unit,
        maxValueUnitConversion?.Unit
      );
    };
    let electricityData: IAny;
    const hasBaseline =
      showChangeWhenData &&
      (isNaN(Number(element.totalConsumption)) ||
        isNaN(Number(element.totalBaselineConsumption)) ||
        Number(element.totalConsumption) === 0 ||
        Number(element.totalBaselineConsumption) === 0 ||
        Math.abs(Number(element.totalEnergyDeviationPercentage)) >
          PERCENT_CHANGE_THRESHOLD);

    electricityData = {
      totalActualEmission: element.totalConsumption,
      totalPreviousPeriodEmission: element.totalBaselineConsumption,
      totalAbsoluteConsumption: convertElectricValue(
        element.totalAbsoluteConsumption
      ),
      totalAbsoluteBaselineConsumption: convertElectricValue(
        element.totalAbsoluteBaselineConsumption
      ),
      percentageEmissionsSaving:
        element.totalEnergyDeviationPercentage === null
          ? null
          : parseFloat(
              element.totalEnergyDeviationPercentage?.toFixed(
                KPI_PERCENTAGE_DECIMAL_PRECISION
              )
            ),
      totalDeviation: hasBaseline ? null : element.totalEnergyDeviation,

      actualUnit: getMaxConvertedValueFromArray(
        ConversionType.ELECTRICITY,
        fromUnit, //element.unit,
        [element.totalConsumption]
      ).Unit,
      baselineUnit: getMaxConvertedValueFromArray(
        ConversionType.ELECTRICITY,
        fromUnit, //element.unit,
        [element.totalBaselineConsumption]
      ).Unit,
      changeUnit: getMaxConvertedValueFromArray(
        ConversionType.ELECTRICITY,
        fromUnit, //element.unit,
        [element.totalEnergyDeviation]
      ).Unit,
      rawTotalActualEmission: element.totalConsumption,
      rawTotalPreviousPeriodEmission: element.totalBaselineConsumption,
      rawTotalAbsoluteConsumption: element.totalAbsoluteConsumption,
      rawTotalAbsoluteBaselineConsumption:
        element.totalAbsoluteBaselineConsumption,
      rawTotalDeviation: hasBaseline ? null : element.totalEnergyDeviation,
      utilityType: element.utilityType,
      utilityMeters: utilityTypeMeterDetails.electricityMeters,
      unit: maxValueUnitConversion.Unit
    };
    if (
      electricityData.totalDeviation === 0 &&
      element.totalEnergyDeviation !== 0
    ) {
      electricityData = {
        ...electricityData,
        deviationScaledData: getConvertedValue(
          ConversionType?.ELECTRICITY,
          element?.unit,
          element.totalEnergyDeviation,
          maxValueUnitConversion.Unit
        )
      };
    }
    utilityMeterDetail.push(electricityData as IUtilityArea);
  }

  return utilityMeterDetail;
};

export const getEnergyTotalUsagesMeterDetails = (
  utilityMeterDetails: Array<ITotalConsumeUtilitydetails>,
  fromUnit: string,
  showChangeWhenData: boolean = false
) => {
  let utilityMeterDetail: Array<IUtilityArea> = [];
  utilityMeterDetails &&
    utilityMeterDetails.length &&
    utilityMeterDetails.forEach((data) => {
      const { meterSavingdata, utilityTypeMeterDetails } = data;

      for (const element of meterSavingdata) {
        const valuesArray = [
          element.totalConsumption,
          element.totalBaselineConsumption
        ];
        // if (
        //   element.utilityType.toLowerCase() === EnergyUsageScopeType.ELECTRIC ||
        //   element.utilityType === EnergyUsageScopeType.ELECTRICITY
        // ) {
        //   const maxValueUnitConversion = getMaxConvertedValueFromArray(
        //     ConversionType.ELECTRICITY,
        //     element.unit,
        //     valuesArray
        //   );
        //   const convertElectricValue = (value: number | null) => {
        //     return getConvertedValuesViewDetails(
        //       value,
        //       ConversionType?.ELECTRICITY,
        //       element?.unit,
        //       maxValueUnitConversion?.Unit
        //     );
        //   };
        //   let electricityData;
        //   electricityData = {
        //     totalActualEmission: convertElectricValue(element.totalConsumption),
        //     totalPreviousPeriodEmission: convertElectricValue(
        //       element.totalBaselineConsumption
        //     ),
        //     totalAbsoluteConsumption: convertElectricValue(
        //       element.totalAbsoluteConsumption
        //     ),
        //     totalAbsoluteBaselineConsumption: convertElectricValue(
        //       element.totalAbsoluteBaselineConsumption
        //     ),
        //     percentageEmissionsSaving:
        //       element.totalEnergyDeviationPercentage === null
        //         ? null
        //         : parseFloat(
        //             element.totalEnergyDeviationPercentage?.toFixed(
        //               KPI_PERCENTAGE_DECIMAL_PRECISION
        //             )
        //           ),
        //     totalDeviation: convertElectricValue(element.totalEnergyDeviation),
        //     rawTotalActualEmission: element.totalConsumption,
        //     rawTotalPreviousPeriodEmission: element.totalBaselineConsumption,
        //     rawTotalAbsoluteConsumption: element.totalAbsoluteConsumption,
        //     rawTotalAbsoluteBaselineConsumption:
        //       element.totalAbsoluteBaselineConsumption,
        //     rawTotalDeviation: element.totalEnergyDeviation,
        //     utilityType: element.utilityType,
        //     utilityMeters: utilityTypeMeterDetails.electricityMeters,
        //     unit: maxValueUnitConversion.Unit
        //   };
        //   if (
        //     electricityData.totalDeviation === 0 &&
        //     element.totalEnergyDeviation !== 0
        //   ) {
        //     electricityData = {
        //       ...electricityData,
        //       deviationScaledData: getConvertedValue(
        //         ConversionType?.ELECTRICITY,
        //         element?.unit,
        //         element.totalEnergyDeviation
        //       )
        //     };
        //   }
        //   utilityMeterDetail.push(electricityData as IUtilityArea);
        // }
        utilityMeterDetail = getEnergyUsageScope(
          element,
          utilityTypeMeterDetails,
          utilityMeterDetail,
          fromUnit,
          showChangeWhenData
        );
        if (element.utilityType.toLowerCase() === 'gas') {
          const maxValueUnitConversion = getMaxConvertedValueFromArray(
            ConversionType.GAS,
            element.unit,
            valuesArray
          );
          const convertGasValue = (value: number | null) => {
            return getConvertedValuesViewDetails(
              value,
              ConversionType?.GAS,
              element?.unit,
              maxValueUnitConversion?.Unit
            );
          };
          let gasData: IAny;
          const hasBaseline =
            showChangeWhenData &&
            (isNaN(Number(element.totalConsumption)) ||
              isNaN(Number(element.totalBaselineConsumption)) ||
              Number(element.totalConsumption) === 0 ||
              Number(element.totalBaselineConsumption) === 0 ||
              Math.abs(Number(element.totalEnergyDeviationPercentage)) >
                PERCENT_CHANGE_THRESHOLD);
          gasData = {
            totalActualEmission: element.totalConsumption,
            totalPreviousPeriodEmission: element.totalBaselineConsumption,
            totalAbsoluteConsumption: convertGasValue(
              element.totalAbsoluteConsumption
            ),
            totalAbsoluteBaselineConsumption: convertGasValue(
              element.totalAbsoluteBaselineConsumption
            ),
            percentageEmissionsSaving:
              element.totalEnergyDeviationPercentage === null
                ? null
                : parseFloat(
                    element.totalEnergyDeviationPercentage?.toFixed(
                      KPI_PERCENTAGE_DECIMAL_PRECISION
                    )
                  ),
            totalDeviation: hasBaseline ? null : element.totalEnergyDeviation,
            actualUnit: getMaxConvertedValueFromArray(
              ConversionType.GAS,
              fromUnit,
              [element.totalConsumption]
            ).Unit,
            baselineUnit: getMaxConvertedValueFromArray(
              ConversionType.GAS,
              fromUnit,
              [element.totalBaselineConsumption]
            ).Unit,
            changeUnit: getMaxConvertedValueFromArray(
              ConversionType.GAS,
              fromUnit,
              [element.totalEnergyDeviation]
            ).Unit,
            rawTotalActualEmission: element.totalConsumption,
            rawTotalPreviousPeriodEmission: element.totalBaselineConsumption,
            rawTotalAbsoluteConsumption: element.totalAbsoluteConsumption,
            rawTotalAbsoluteBaselineConsumption:
              element.totalAbsoluteBaselineConsumption,
            rawTotalDeviation: hasBaseline
              ? null
              : element.totalEnergyDeviation,
            utilityType: element.utilityType,
            utilityMeters: utilityTypeMeterDetails.gasMeters,
            unit: maxValueUnitConversion.Unit
          };
          if (
            gasData.totalDeviation === 0 &&
            element.totalEnergyDeviation !== 0
          ) {
            gasData = {
              ...gasData,
              deviationScaledData: getConvertedValue(
                ConversionType?.GAS,
                element?.unit,
                element.totalEnergyDeviation
              )
            };
          }
          utilityMeterDetail.push(gasData as IUtilityArea);
        }
      }
    });
  utilityMeterDetail =
    utilityMeterDetail && utilityMeterDetail.length
      ? utilityMeterDetail.map((meterDetails) => {
          return {
            ...meterDetails,
            utilityMeters: meterDetails.utilityMeters.map((meter) => {
              return {
                ...meter,
                progessBarRelativeWidth:
                  meterDetails.rawTotalActualEmission >
                  meterDetails.rawTotalPreviousPeriodEmission
                    ? meterDetails.rawTotalActualEmission
                    : meterDetails.rawTotalPreviousPeriodEmission
              };
            })
          };
        })
      : [];
  return utilityMeterDetail;
};

export const getMaxBreakdownByMeter = (
  data: IEnergyMeter[] | AreaWiseAggregations[] | IBreakdownByMeterProps[]
) => {
  let maxUnit = 0;
  if (data) {
    data.forEach((element) => {
      const maxValue = Math.max(
        element.totalConsumption !== null ? element.totalConsumption : 0,
        element.totalBaselineConsumption !== null
          ? element.totalBaselineConsumption
          : 0
      );
      if (maxValue > maxUnit) {
        maxUnit = maxValue;
      }
    });
  }
  return maxUnit;
};

export const getMaxBreakdownByMeterForBasic = (data: IEnergyMeter[]) => {
  let maxUnit = 0;
  if (data) {
    data.forEach((element) => {
      const maxValue = Math.max(
        element.totalConsumption !== null ? element.totalConsumption : 0,
        element.totalBaselineConsumption !== null
          ? element.totalBaselineConsumption
          : 0
      );
      if (maxValue > maxUnit) {
        maxUnit = maxValue;
      }
    });
  }
  return maxUnit;
};

const getMaxBreakdownByFloor = (data: IEnergyMeter[]) => {
  let maxUnit = 0;
  if (data) {
    data.forEach((element) => {
      const maxValue = Math.max(
        element.totalConsumption !== null ? element.totalConsumption : 0,
        element.totalBaselineConsumption !== null
          ? element.totalBaselineConsumption
          : 0
      );
      if (maxValue > maxUnit && element.name !== 'HIST_1_MM') {
        maxUnit = maxValue;
      }
    });
  }
  return maxUnit;
};
export const convertDataForEnergyUsagesSavingsMeter = (
  utilityMeters: Array<IEnergyMeter>,
  originalUnit: string,
  unit: string,
  showChangeWhenData: boolean = false
) => {
  let meterSavingsData: Array<IEnergyBreakdownMeter> = [];
  let convertedUnit: string = '';
  let conversionType = ConversionType.ELECTRICITY;
  if (
    utilityMeters &&
    utilityMeters.length > 0 &&
    utilityMeters[0]?.sourceType.toLowerCase() === 'gas'
  ) {
    conversionType = ConversionType.GAS;
  }
  if (
    utilityMeters &&
    utilityMeters.length > 0 &&
    utilityMeters[0]?.sourceType.toLowerCase() === 'water'
  ) {
    conversionType = ConversionType.WATER;
  }
  const valuesArray: number[] = [];
  utilityMeters &&
    utilityMeters.length &&
    utilityMeters.forEach((meter) => {
      valuesArray.push(
        meter.totalConsumption!,
        meter.totalBaselineConsumption!
      );
    });

  const maxValueUnitConversion = getMaxConvertedValueFromArray(
    conversionType,
    conversionType === ConversionType.ELECTRICITY ? unit : originalUnit,
    valuesArray,
    originalUnit
  );
  utilityMeters &&
    utilityMeters.length &&
    utilityMeters.forEach((data) => {
      const {
        name,
        id,
        sourceType,
        totalConsumption,
        totalBaselineConsumption,
        totalEnergyDeviation,
        totalEnergyDeviationPercentage,
        totalAbsoluteConsumption,
        totalAbsoluteBaselineConsumption
      } = data;

      const hasBaseline =
        showChangeWhenData &&
        (isNaN(Number(totalConsumption)) ||
          isNaN(Number(totalBaselineConsumption)) ||
          Number(totalConsumption) === 0 ||
          Number(totalBaselineConsumption) === 0 ||
          Math.abs(Number(totalEnergyDeviationPercentage)) >
            PERCENT_CHANGE_THRESHOLD);

      convertedUnit = maxValueUnitConversion.Unit;
      const getConvertedValueforMeter = (value: number | null) => {
        if (value === null) return null;
        else
          return getConvertedValue(
            conversionType,
            originalUnit,
            value,
            maxValueUnitConversion.Unit
          ).Value;
      };
      let meterSavings: IEnergyBreakdownMeter = {
        meter: name ? name : id,
        actualConsumption: totalConsumption,
        baselineConsumption: totalBaselineConsumption,
        actualUnit: getMaxConvertedValueFromArray(
          conversionType,
          conversionType === ConversionType.ELECTRICITY ? unit : originalUnit,
          [totalConsumption],
          originalUnit
        ).Unit,
        baselineUnit: getMaxConvertedValueFromArray(
          conversionType,
          conversionType === ConversionType.ELECTRICITY ? unit : originalUnit,
          [totalBaselineConsumption],
          originalUnit
        ).Unit,
        changeUnit: getMaxConvertedValueFromArray(
          conversionType,
          conversionType === ConversionType.ELECTRICITY ? unit : originalUnit,
          [totalEnergyDeviation],
          originalUnit
        ).Unit,
        totalAbsoluteConsumption: getConvertedValueforMeter(
          totalAbsoluteConsumption
        ),
        totalAbsoluteBaselineConsumption: getConvertedValueforMeter(
          totalAbsoluteBaselineConsumption
        ),
        change: hasBaseline ? null : totalEnergyDeviation,
        percentageConsumptionSaving:
          totalEnergyDeviationPercentage === null
            ? null
            : parseFloat(
                totalEnergyDeviationPercentage?.toFixed(
                  KPI_PERCENTAGE_DECIMAL_PRECISION
                )
              ),
        rawActualConsumption: totalConsumption,
        rawBaselineConsumption: totalBaselineConsumption,
        rawTotalAbsoluteConsumption: totalAbsoluteConsumption,
        rawTotalAbsoluteBaselineConsumption: totalAbsoluteBaselineConsumption,
        rawChange: hasBaseline ? null : totalEnergyDeviation,
        utilityType: sourceType
      };
      if (meterSavings.change === 0 && totalEnergyDeviation !== 0) {
        meterSavings = {
          ...meterSavings,
          deviationScaledData: getConvertedValue(
            conversionType,
            originalUnit,
            totalEnergyDeviation,
            maxValueUnitConversion.Unit
          )
        };
      }
      meterSavingsData.push(meterSavings);
    });
  let maxRelativeValue = Number.MIN_VALUE;
  meterSavingsData &&
    meterSavingsData.length &&
    meterSavingsData.forEach((meter) => {
      const tempMax = getMax(
        meter.rawActualConsumption!,
        meter.rawBaselineConsumption!
      );
      maxRelativeValue = getMax(tempMax, maxRelativeValue);
    });
  meterSavingsData =
    meterSavingsData && meterSavingsData.length
      ? meterSavingsData.map((meterdata) => {
          return {
            ...meterdata,
            progessBarRelativeWidth: maxRelativeValue
          };
        })
      : [];

  return { values: meterSavingsData, displayUnit: convertedUnit };
};
export const getMax = (number1: number, number2: number) => {
  if (number1 > number2) return number1;
  else return number2;
};
export const convertDataForEnergyDetailSavingsMeter = (
  emissionsByUtility: Array<IEmissionsByUtility>
) => {
  const meterSavingsData: Array<IEmissionsByUtility> = [];
  let utilityUnit: IObject = {};
  //let conversionUnit: IObject = {};
  emissionsByUtility &&
    emissionsByUtility.length &&
    emissionsByUtility.forEach((data) => {
      const {
        totalConsumption,
        totalBaselineConsumption,
        totalEnergyDeviation,
        totalEnergyDeviationPercentage,
        sourceType,
        measurementUnit,
        totalAbsoluteConsumption,
        totalAbsoluteBaselineConsumption
      } = data;
      // const maxConversionUnit = getMaxConvertedValueFromArray(
      //   sourceType.toLowerCase() === 'gas'
      //     ? ConversionType.GAS
      //     : ConversionType.ELECTRICITY,
      //   measurementUnit,
      //   [totalConsumption, totalBaselineConsumption]
      // ).Unit;
      let meterSavings;
      meterSavings = {
        totalConsumption,
        totalBaselineConsumption,
        totalEnergyDeviation,
        totalEnergyDeviationPercentage: totalEnergyDeviationPercentage,
        utilityType: sourceType,
        unit: measurementUnit,
        totalAbsoluteConsumption,
        totalAbsoluteBaselineConsumption
      };
      meterSavingsData.push(meterSavings as IEmissionsByUtility);
      const key = sourceType?.toLowerCase();
      utilityUnit[key] = measurementUnit;
      //conversionUnit[key] = maxConversionUnit;
    });
  return {
    energyMeterSavingdata: meterSavingsData,
    utilityUnit: utilityUnit
    //conversionUnit
  };
};
export const getDateLabel = (
  startMonth: string,
  endMonth: string,
  performanceAggregationApplied: string,
  startYear: string,
  endYear: string,
  startDate: string,
  endDate: string
) => {
  if (
    performanceAggregationApplied?.toLowerCase() == 'hourly' &&
    startDate !== endDate
  )
    return `${startDate} - ${endDate} ${startMonth} ${startYear}`;
  else if (
    performanceAggregationApplied?.toLowerCase() == 'hourly' &&
    startDate === endDate
  )
    return `${startDate} ${startMonth} ${startYear}`;
  else if (startYear != endYear)
    return `${startMonth} ${startYear} - ${endMonth} ${endYear}`;
  else if (startMonth != endMonth && startYear === endYear)
    return `${startMonth} - ${endMonth} ${endYear}`;
  else return `${startMonth} ${endYear}`;
};

export const convertChartPremiumToBasic = (
  monthWiseAggregations: Array<IBasicMonthWise>,
  fromUnit: string,
  conversionType: string,
  originalUnit: string,
  lng?: string
) => {
  const valuesArray: number[] = [];
  monthWiseAggregations.forEach((monthdata) => {
    valuesArray.push(
      ...[monthdata.totalBaselineConsumption, monthdata.totalConsumption]
    );
  });
  const toUnit = getMaxConvertedValueFromArray(
    conversionType,
    conversionType.toLowerCase() === 'gas' ? originalUnit : fromUnit,
    valuesArray,
    originalUnit
  ).Unit;
  let value = graphValue(monthWiseAggregations, lng);
  let YearIndex: Array<string> = [];
  let MonthIndex: Array<string> = [];
  let actualConsumptionData: Array<number | null> = [];
  let baselineConsumptionData: Array<number | null> = [];
  let SiteWithDataCount: Array<number | null> = [];
  let IsCompleted: Array<boolean> = [];
  let PeriodUpto: Array<number> = [];
  let noDataActual: boolean = false;
  let noDataPrevious: boolean = false;

  monthWiseAggregations.forEach(
    ({
      totalConsumption,
      totalBaselineConsumption,
      isCompleted,
      consumptionProcessedDays,
      sitesWithData
    }) => {
      if (totalConsumption == null || totalConsumption == 0) {
        actualConsumptionData.push(0);
        noDataActual = true;
      } else {
        const unitScaledActual = getConvertedValue(
          conversionType,
          originalUnit,
          totalConsumption,
          toUnit
        ).Value;
        actualConsumptionData.push(unitScaledActual);
      }
      if (totalBaselineConsumption == null || totalBaselineConsumption == 0) {
        baselineConsumptionData.push(0);
        noDataPrevious = true;
      } else {
        const unitScaledPreviousPeriod = getConvertedValue(
          conversionType,
          originalUnit,
          totalBaselineConsumption,
          toUnit
        ).Value;
        baselineConsumptionData.push(unitScaledPreviousPeriod);
      }

      IsCompleted.push(isCompleted);
      PeriodUpto.push(consumptionProcessedDays);
      SiteWithDataCount.push(sitesWithData || null);
      YearIndex = value.yearIndex;
      MonthIndex = value.monthIndex;
    }
  );
  return {
    actualConsumptionData,
    baselineConsumptionData,
    IsCompleted,
    SiteWithDataCount,
    YearIndex,
    MonthIndex,
    PeriodUpto,
    noDataActual,
    noDataPrevious,
    chartUnit: toUnit
  };
};

export const convertStackedDataForEnergyDisaggregationChart = (
  energyDisaggregation: Array<EnergyDisaggregation>,
  fromUnit: string,
  originalUnit: string,
  requestContext?: IAny,
  lng?: string
) => {
  const interValType = getIntervalTypeForEnergyDisaggregation(
    requestContext.context.startTime as string,
    requestContext.context.endTime as string
  );

  let MonthYearIndex;
  let checkStartEndDate: IAny[] | undefined;
  if (interValType === IntervalType.Monthly) {
    checkStartEndDate = checkStartEndDateMonth(
      energyDisaggregation,
      requestContext
    );
    MonthYearIndex = graphValueNILM(
      checkStartEndDate,
      IntervalType.Monthly,
      lng
    );
  } else if (interValType === IntervalType.Daily) {
    checkStartEndDate = checkStartEndDateDays(
      energyDisaggregation,
      requestContext
    );
    MonthYearIndex = graphValueNILM(checkStartEndDate, IntervalType.Daily, lng);
  } else {
    checkStartEndDate = checkStartEndDateHourly(
      energyDisaggregation,
      requestContext
    );
    MonthYearIndex = graphValueNILMHourly(checkStartEndDate, requestContext);
  }

  const maxValuesArray: number[] = [];
  checkStartEndDate?.forEach((monthData) => {
    maxValuesArray.push(
      monthData.totalConsumption,
      monthData.baselineConsumption
    );
  });
  const maxValueUnitConversion = getMaxConvertedValueFromArray(
    ConversionType.ELECTRICITY,
    fromUnit,
    maxValuesArray,
    originalUnit
  );

  const scaledUnit = (value: number) =>
    getConvertedValue(
      ConversionType.ELECTRICITY,
      originalUnit, //fromUnit
      value,
      maxValueUnitConversion.Unit
    ).Value;
  let YearIndex: Array<string> | undefined = [];
  let MonthIndex: Array<string> | undefined = [];
  YearIndex = MonthYearIndex?.yearIndex;
  MonthIndex = MonthYearIndex?.monthIndex;

  let uniqueFuelTypes: string[] = [];
  checkStartEndDate?.forEach((monthData) => {
    monthData.disaggregation?.forEach(
      (subUtilityEmission: { name: string }) => {
        if (!uniqueFuelTypes.includes(subUtilityEmission.name))
          uniqueFuelTypes.push(subUtilityEmission.name);
      }
    );
  });
  uniqueFuelTypes.push('TotalConsuption');
  const fuel = uniqueFuelTypes.map((fuel, index) => {
    const { actual, previous } = getMonthwiseDataForEnergyDisaggregation(
      fuel,
      checkStartEndDate as IAny,
      scaledUnit
    );
    return {
      name: fuel,
      color:
        fuel === 'TotalConsuption'
          ? ''
          : uniqueFuelTypes.length <= MAGIC_VALUE.SIX
          ? FuelColorMappingFor6[index]
          : FuelColorMapping[getColorIndex(index)],
      actual,
      previous
    };
  });

  return {
    MonthIndex,
    YearIndex,
    fuel,
    unit: maxValueUnitConversion.Unit
  };
};

export const convertEnergyDisaggregationTableChart = (
  energyDisaggregation: Array<EnergyDisaggregation>,
  fromUnit: string,
  originalUnit: string,
  requestContext?: IAny,
  lng?: string
) => {
  const maxValuesArray: number[] = [];

  const interValType = getIntervalTypeForEnergyDisaggregation(
    requestContext.context.startTime as string,
    requestContext.context.endTime as string
  );
  let checkStartEndDate: IAny[] | undefined;
  if (interValType === IntervalType.Monthly) {
    checkStartEndDate = checkStartEndDateMonth(
      energyDisaggregation,
      requestContext
    );
  } else if (interValType === IntervalType.Daily) {
    checkStartEndDate = checkStartEndDateDays(
      energyDisaggregation,
      requestContext
    );
  } else {
    checkStartEndDate = checkStartEndDateHourly(
      energyDisaggregation,
      requestContext
    );
  }
  checkStartEndDate?.forEach((monthData) => {
    maxValuesArray.push(
      monthData.totalConsumption,
      monthData.baselineConsumption
    );
  });
  const maxValueUnitConversion = getMaxConvertedValueFromArray(
    ConversionType.ELECTRICITY,
    fromUnit,
    maxValuesArray,
    originalUnit
  );
  const scaledUnit = (value: number) =>
    getConvertedValue(
      ConversionType.ELECTRICITY,
      originalUnit, //fromUnit,
      value,
      maxValueUnitConversion.Unit
    ).Value;

  let value;
  if (interValType === IntervalType.Monthly) {
    value = tableValueNILM(
      checkStartEndDate as IAny,
      maxValueUnitConversion.Unit,
      scaledUnit,
      IntervalType.Monthly,
      lng
    );
  } else if (interValType === IntervalType.Daily) {
    value = tableValueNILM(
      checkStartEndDate as IAny,
      maxValueUnitConversion.Unit,
      scaledUnit,
      IntervalType.Daily,
      lng
    );
  } else {
    value = tableValueNILMHourly(
      checkStartEndDate as IAny,
      maxValueUnitConversion.Unit,
      scaledUnit,
      requestContext
    );
  }

  let YearIndex: Array<string> | undefined = [];
  YearIndex = value?.yearIndex;

  let uniqueFuelTypes: string[] = [];
  checkStartEndDate?.forEach((monthData) => {
    monthData.disaggregation?.forEach(
      (subUtilityEmission: { name: string }) => {
        if (!uniqueFuelTypes.includes(subUtilityEmission.name))
          uniqueFuelTypes.push(subUtilityEmission.name);
      }
    );
  });

  return {
    value,
    YearIndex,
    unit: maxValueUnitConversion.Unit
  };
};

export const isNull = (value: number | null) => {
  if (value === null) {
    return null;
  }
  return value;
};

export const getConvertedValues = (
  value: number | null,
  deviationScaledData?: scaledData
) => {
  if (value === 0 && deviationScaledData && deviationScaledData.Value !== 0) {
    return getAbsoluteValue(
      JSON.parse(deviationScaledData.Value!.toString())?.toFixed(
        KPI_VALUE_DECIMAL_PRECISION
      )
    );
  } else if (value === undefined || value === null) {
    return '--';
  } else
    return getAbsoluteValue(
      JSON.parse(value.toString())?.toFixed(KPI_VALUE_DECIMAL_PRECISION)
    );
};

export const getConvertedUnit = (
  value: number | null,
  measurementUnit: string | undefined,
  deviationScaledData?: scaledData
) => {
  if (value === 0 && deviationScaledData && deviationScaledData.Value !== 0) {
    return deviationScaledData.Unit;
  } else if (value === null) {
    return ' ';
  } else return measurementUnit;
};

export const getNullToZero = (value: number | null): number => {
  if (value === null) {
    return 0;
  } else return value;
};

export const getNullConverted = (value: number | null): string | number => {
  if (value === null) {
    return '--';
  } else return value;
};

export const getNullConvertedValueForEnergyCardTable = (
  value: number | null | string
): string | number => {
  if (value === null || (value && value.toString() === Number(0).toString())) {
    return '--';
  }
  return value;
};

// export const filterNullValue = (value: (number | null)[]): number[] => {
//   let x = value.filter((num) => {
//     if (num) return num;
//   });
//   return x as number[];
// };

export const filterNullValue = (value: (number | null)[]): number[] => {
  let x;
  if (value) {
    x = value.filter((num) => {
      if (num) return num;
    });
  }
  return x as number[];
};

export const checkApiResponseNullValue = (
  apiValue: null | number,
  operatedValue: number | null
) => {
  if (apiValue === null) {
    return apiValue;
  } else {
    return operatedValue;
  }
};

export const getNullCheckedAggregationSummaryData = (
  aggregationSummaryData: IAggregationSummaryData,
  summary: ISummaryData,
  showChangeWhenData: boolean = false
): IAggregationSummaryData => {
  const hasBaseline =
    showChangeWhenData &&
    (isNaN(Number(summary.totalConsumption)) ||
      isNaN(Number(summary.totalBaselineConsumption)) ||
      Number(summary.totalConsumption) === 0 ||
      Number(summary.totalBaselineConsumption) === 0 ||
      Math.abs(Number(summary.totalEnergyDeviationPercentage)) >
        PERCENT_CHANGE_THRESHOLD);

  aggregationSummaryData.actualCost = checkApiResponseNullValue(
    summary.totalConsumption,
    aggregationSummaryData.actualCost
  );

  aggregationSummaryData.baselineCost = checkApiResponseNullValue(
    summary.totalBaselineConsumption,
    aggregationSummaryData.baselineCost
  );
  aggregationSummaryData.costSavings = hasBaseline
    ? null
    : checkApiResponseNullValue(
        summary.totalEnergyDeviation,
        aggregationSummaryData.costSavings
      );
  aggregationSummaryData.costSavingsPercentage = checkApiResponseNullValue(
    summary.totalEnergyDeviationPercentage,
    aggregationSummaryData.costSavingsPercentage
  );
  return aggregationSummaryData;
};

export const getNullCheckedAggregationEnergyCardData = (
  aggregationEnergyData: IAggregationEnergyData,
  summary: ISummaryData,
  showChangeWhenData: boolean = false
): IAggregationEnergyData => {
  const hasBaseline =
    showChangeWhenData &&
    (isNaN(Number(aggregationEnergyData.actual)) ||
      isNaN(Number(aggregationEnergyData.previousPeriod)) ||
      Number(aggregationEnergyData.actual) === 0 ||
      Number(aggregationEnergyData.previousPeriod) === 0 ||
      Math.abs(Number(aggregationEnergyData.savingsPercentage)) >
        PERCENT_CHANGE_THRESHOLD);

  aggregationEnergyData.actual = checkApiResponseNullValue(
    summary.totalConsumption,
    aggregationEnergyData.actual
  );
  aggregationEnergyData.absoluteActual = checkApiResponseNullValue(
    summary.absoluteTotalConsumption,
    aggregationEnergyData.absoluteActual
  );
  aggregationEnergyData.previousPeriod = checkApiResponseNullValue(
    summary.totalBaselineConsumption,
    aggregationEnergyData.previousPeriod
  );
  aggregationEnergyData.absolutePrevPeriod = checkApiResponseNullValue(
    summary.absoluteTotalBaselineConsumption,
    aggregationEnergyData.absolutePrevPeriod
  );
  aggregationEnergyData.savings = hasBaseline
    ? null
    : checkApiResponseNullValue(
        summary.totalEnergyDeviation,
        aggregationEnergyData.savings
      );
  aggregationEnergyData.savingsPercentage = checkApiResponseNullValue(
    summary.totalEnergyDeviationPercentage,
    aggregationEnergyData.savingsPercentage
  );
  return aggregationEnergyData;
};

export const headersEnergyUsageBasic = (
  t: TFunction,
  viewType: string | undefined,
  totalSiteCount: number,
  displayUnit: string,
  usageType?: string,
  euiUnit?: string
): IDataTableHeader[] => {
  if (viewType && viewType == EnergyUsageField.SITEENERGY) {
    return [
      {
        HeaderValue: t(d.BREAKDOWNMETER),
        TextAlignValue: 'left',
        Field: 'type'
      },
      {
        HeaderValue: usageType!,
        TextAlignValue: 'right',
        Field: 'high',
        sortable: true
      },
      {
        HeaderValue: t(d.CHANGE),
        TextAlignValue: 'right',
        Field: 'change',
        sortable: true
      }
    ];
  }
  if (viewType && viewType == EnergyUsageField.PORTFOLIOENERGY) {
    return [
      {
        HeaderValue: t(d.PORTFOLIOSITECOUNT, {
          count: totalSiteCount
        }),
        TextAlignValue: 'left',
        Field: 'type'
      },
      {
        HeaderValue: t(d.PORTFOLIOENERGYCONS),
        TextAlignValue: 'right',
        Field: 'high',
        sortable: true
      },
      {
        HeaderValue: t(d.PORTFOLIOEUI, {
          unit: euiUnit
        }),
        TextAlignValue: 'right',
        Field: 'eui',
        sortable: true
      }
    ];
  }
  if (viewType && viewType == EnergyUsageField.PORTFOLIOWATER) {
    return [
      {
        HeaderValue: t(d.PORTFOLIOSITECOUNT, {
          count: totalSiteCount
        }),
        TextAlignValue: 'left',
        Field: 'type'
      },
      {
        HeaderValue: t(d.PORTFOLIOWATERCONS),
        TextAlignValue: 'right',
        Field: 'high',
        sortable: true
      },
      {
        HeaderValue: t(d.PORTFOLIOWATERChange),
        TextAlignValue: 'right',
        Field: 'change',
        sortable: true
      }
    ];
  }
  return [];
};

export const convertDataforPortfolioBreakdownByMeter = (
  breakdownBySite: Array<ISiteEnergyUsageData> | undefined,
  unit: string,
  euiMeasurementUnit: string,
  preferredEuiUnit: string,
  summaryValues: (number | null)[] = [],
  originalUnit: string
) => {
  const valuesArray = getEnergyTotalUsagesValues(
    (breakdownBySite as unknown) as IBreakdownByMeterProps[]
  );
  const maxValueUnitConversion = getMaxConvertedValueFromArray(
    ConversionType.ELECTRICITY,
    unit,
    [...valuesArray, ...summaryValues],
    originalUnit
  );
  const EnergyBreakdownData: Array<IPortfolioMeterDataProps> = [];

  breakdownBySite?.forEach((data) => {
    const {
      id,
      name,
      totalConsumption,
      actualEui,
      totalEnergyDeviation
    } = data;
    const energy = getConvertedValue(
      ConversionType.ELECTRICITY,
      originalUnit,
      totalConsumption,
      maxValueUnitConversion.Unit
    );

    let convertedEui = null;

    if (actualEui && euiMeasurementUnit) {
      convertedEui = handleEuiUnitConversion(
        actualEui,
        euiMeasurementUnit,
        preferredEuiUnit || ''
      );
      convertedEui = parseFloat(
        convertedEui.toFixed(KPI_VALUE_DECIMAL_PRECISION)
      );
    }

    let meterConsumptions: IPortfolioMeterDataProps = {
      id: id,
      type: name,
      high: totalConsumption !== null && energy ? energy?.Value : null,
      eui: convertedEui,
      totalDeviation: totalEnergyDeviation
    };
    EnergyBreakdownData.push(meterConsumptions);
  });

  return {
    values: EnergyBreakdownData,
    displayUnit: maxValueUnitConversion.Unit
  };
};

export const convertDataforPortfolioEnergyBreakdownBySite = (
  breakdownBySite: Array<ISiteEnergyUsageData> | undefined,
  unit: string,
  euiMeasurementUnit: string,
  preferredEuiUnit: string,
  summaryValues: (number | null)[] = [],
  originalUnit: string,
  showChangeWhenData: boolean = false,
  conversionType: string = ConversionType.ELECTRICITY
) => {
  // const valuesArray = getEnergyTotalUsagesValues(
  //   (breakdownBySite as unknown) as IBreakdownByMeterProps[]
  // );
  // const maxValueUnitConversion = getMaxConvertedValueFromArray(
  //   ConversionType.ELECTRICITY,
  //   unit,
  //   [...valuesArray, ...summaryValues],
  //   originalUnit
  // );
  const EnergyBreakdownData: Array<IPortfolioMeterDataProps> = [];

  breakdownBySite?.forEach((data) => {
    const {
      id,
      name,
      totalConsumption,
      actualEui,
      totalEnergyDeviation,
      totalBaselineConsumption,
      totalEnergyDeviationPercentage
    } = data;
    const energy = totalConsumption;

    let convertedEui = null;

    if (actualEui && euiMeasurementUnit) {
      convertedEui = handleEuiUnitConversion(
        actualEui,
        euiMeasurementUnit,
        preferredEuiUnit || ''
      );
      convertedEui = parseFloat(
        convertedEui.toFixed(KPI_VALUE_DECIMAL_PRECISION)
      );
    }
    const hasBaselineOrConsumptionData =
      showChangeWhenData &&
      (isNaN(Number(totalConsumption)) ||
        isNaN(Number(totalBaselineConsumption)) ||
        Number(totalConsumption) === 0 ||
        Number(totalBaselineConsumption) === 0 ||
        Math.abs(Number(totalEnergyDeviationPercentage)) >
          PERCENT_CHANGE_THRESHOLD);
    let meterConsumptions: IPortfolioMeterDataProps = {
      id: id,
      type: name,
      high: totalConsumption !== null && energy ? energy : null,
      eui: convertedEui,
      totalDeviation:
        showChangeWhenData &&
        (isNaN(Number(totalBaselineConsumption)) ||
          isNaN(Number(totalConsumption)) ||
          Number(totalBaselineConsumption) === 0 ||
          Number(totalConsumption) === 0)
          ? null
          : totalEnergyDeviation,
      change:
        hasBaselineOrConsumptionData || totalEnergyDeviation === null
          ? null
          : totalEnergyDeviation,
      unit: getMaxConvertedValueFromArray(
        conversionType,
        unit,
        [totalConsumption],
        originalUnit
      ).Unit,
      deviationUnit: getMaxConvertedValueFromArray(
        conversionType,
        unit,
        [totalEnergyDeviation],
        originalUnit
      ).Unit,
      totalDeviationPercentage:
        totalEnergyDeviationPercentage === null
          ? null
          : parseFloat(
              totalEnergyDeviationPercentage?.toFixed(
                KPI_PERCENTAGE_DECIMAL_PRECISION
              )
            ),
      conversionType
    };

    EnergyBreakdownData.push(meterConsumptions);
  });

  return {
    values: EnergyBreakdownData
    // displayUnit: maxValueUnitConversion.Unit
  };
};

export const getEndTimeBasedOnSubscription = (
  requestContext: IRequestContext
) => {
  const { subscriptionType, endTime, startTime } = requestContext.context;
  const FormattedEndtime = moment
    .utc(moment(endTime))
    .format('YYYY-MM-DD HH:mm');
  if (subscriptionType === EmaasSubscriptionType.PREMIUM_SUBSCRIPTION) {
    return returnUTCTimeFormat(endTime!);
  }
  return returnUTCTimeFormat(
    moment(excludeCurrentMonth(FormattedEndtime!, startTime!))
      .endOf('day')
      .format('YYYY-MM-DD HH:mm')
  );
};

export const getProgressBarActualValues = (
  utilityCostCurrency: string | undefined,
  value: number | null,
  toFixedValue?: number,
  fromCarbonKPI?: boolean,
  actualUnit?: string,
  originalUnit?: string,
  conversionType?: string
) => {
  if (utilityCostCurrency && value !== null) {
    return formatCurrency(value, utilityCostCurrency, true, toFixedValue);
    // return utilityCostCurrency === 'MYR'
    //   ? formatCurrency(value, utilityCostCurrency, true, toFixedValue)
    //   : formatPriceValue(
    //       formatCurrency(value, utilityCostCurrency, true, toFixedValue)
    //     );
  } else if (value === null) {
    return '--';
  } else {
    return fromCarbonKPI
      ? getConvertedValue(
          conversionType ? conversionType : 'Electricity',
          originalUnit ? originalUnit : 'kilowatt hour',
          value,
          actualUnit
        )?.Value?.toFixed(KPI_VALUE_DECIMAL_PRECISION)
      : value.toFixed(KPI_VALUE_DECIMAL_PRECISION);
  }
};
export const getProgressBarBaselineValues = (
  utilityCostCurrency: string | undefined,
  value: number | null,
  toFixedValue?: number,
  originalUnit?: string,
  toUnit?: string,
  conversionType?: string
) => {
  if (utilityCostCurrency && value !== null) {
    return formatCurrency(value, utilityCostCurrency, true, toFixedValue);
  } else if (value === null) {
    return '--';
  } else {
    return getConvertedValue(
      conversionType ? conversionType : 'Electricity',
      originalUnit ? originalUnit : 'kilowatt hour',
      value,
      toUnit
    )?.Value?.toFixed(KPI_VALUE_DECIMAL_PRECISION);
  }
};

export const getConvertedValuesViewDetails = (
  setValue: number | null,
  ConversionTypeValue: string,
  unit: string,
  maxUnit: string
) => {
  if (setValue !== null) {
    return getConvertedValue(ConversionTypeValue, unit, setValue, maxUnit)
      .Value;
  } else return null;
};

export const getEnergyStartDate = (
  startDate: string,
  subscriptionType: string,
  emaasContext?: IAny,
  isEnergyUsageTab?: boolean,
  isUtilityMeterSource?: boolean
) => {
  if (
    subscriptionType === EmaasSubscriptionType.PREMIUM_SUBSCRIPTION &&
    !isUtilityMeterSource
  ) {
    return startDate;
  } else if (
    ((emaasContext?.isNILMSubscribed && emaasContext?.toggleFeatureNILM2) ||
      (emaasContext?.hasDisaggIntegration &&
        emaasContext?.toggleRedaptiveIntegration)) &&
    subscriptionType === EmaasSubscriptionType.BASIC_SUBSCRIPTION &&
    !isEnergyUsageTab
  ) {
    return moment(new Date(startDate)).format('YYYY-MM-DD');
  } else if (
    subscriptionType === EmaasSubscriptionType.BASIC_SUBSCRIPTION ||
    isUtilityMeterSource
  ) {
    return getFirstDayofSelectedMonth(startDate);
  } else {
    return getFirstDayofSelectedMonth(startDate);
  }
};

export const getEnergyEndDateBasedOnSub = (
  subscriptionType: string,
  endDate: string,
  startDate: string,
  emaasContext?: IAny,
  isEnergyUsageTab?: boolean,
  isUtilityMeterSource?: boolean
) => {
  if (
    subscriptionType === EmaasSubscriptionType.PREMIUM_SUBSCRIPTION &&
    !isUtilityMeterSource
  ) {
    return moment(new Date(endDate)).format('YYYY-MM-DD');
  } else if (
    ((emaasContext?.isNILMSubscribed && emaasContext?.toggleFeatureNILM2) ||
      (emaasContext?.hasDisaggIntegration &&
        emaasContext?.toggleRedaptiveIntegration)) &&
    subscriptionType === EmaasSubscriptionType.BASIC_SUBSCRIPTION &&
    !isEnergyUsageTab
  ) {
    return moment(new Date(endDate)).format('YYYY-MM-DD');
  } else {
    return formattedDate(excludeCurrentMonth(endDate, startDate));
  }
};

export const getMaxValueIndexForField = (
  meterData: IAny[],
  fieldName: string
) => {
  let max = Number.MIN_VALUE;
  let maxFieldValueIndex = 0;
  for (let i = 0; i < meterData.length; i++) {
    if (meterData[i][fieldName] > max) {
      max = meterData[i][fieldName];
      maxFieldValueIndex = i;
    }
  }
  return maxFieldValueIndex;
};

export const totalDeviationPercentageCheck = (
  totalNativeEnergyDeviationPercentage: null | number
) => {
  if (totalNativeEnergyDeviationPercentage === null) {
    return null;
  } else {
    return parseFloat(
      totalNativeEnergyDeviationPercentage?.toFixed(
        KPI_PERCENTAGE_DECIMAL_PRECISION
      )
    );
  }
};

export const convertGasValueCheck = (
  gas: string,
  convertfromunit: string,
  value: number | null,
  unit: string
) => {
  if (!value) return value;
  else {
    return getConvertedValue(gas, convertfromunit, value, unit).Value;
  }
};

export const getEnergyUsageColumnsForNILM = (t: TFunction) => {
  return [
    {
      className: 'datatable-column-meter',
      field: EnergyUsagesDetailExpandMeterField.meter,
      header: t(d.METER),
      sortable: true,
      initialWidth: '6%'
    },
    {
      className: 'datatable-column-actualConsumption',
      field: EnergyUsagesDetailExpandMeterField.actualConsumption,
      header: t(d.actualCons),
      sortable: true,
      initialWidth: '20%'
    },
    {
      className: 'datatable-column-baselineConsumption',
      field: EnergyUsagesDetailExpandMeterField.baselineConsumption,
      header: t(d.baselineCons),
      sortable: true,
      initialWidth: '9%'
    },
    {
      className: 'datatable-column-change',
      field: EnergyUsagesDetailExpandMeterField.change,
      header: t(d.CHANGE),
      sortable: true,
      initialWidth: '9%'
    }
  ];
};

export const getEnergyColumns = (t: TFunction, subscriptionType: string) => {
  return [
    {
      className: 'datatable-column-meter',
      field: EnergyUsagesDetailExpandMeterField.meter,
      header: t(d.METER),
      sortable: true,
      initialWidth: '6%'
    },
    {
      className: 'datatable-column-actualConsumption',
      field: EnergyUsagesDetailExpandMeterField.actualConsumption,
      header: t(d.actualCons),
      sortable: true,
      initialWidth: '20%'
    },
    {
      className: 'datatable-column-baselineConsumption',
      field: EnergyUsagesDetailExpandMeterField.baselineConsumption,
      header:
        subscriptionType === EnergyUsageField.BASICSUBS
          ? t(d.PREVSPER)
          : t(d.baselineCons),
      sortable: true,
      initialWidth: '9%'
    },
    {
      className: 'datatable-column-change',
      field: EnergyUsagesDetailExpandMeterField.change,
      header: t(d.CHANGE),
      sortable: true,
      initialWidth: '9%'
    }
  ];
};
